import React, { useEffect, useState, useRef } from 'react';
import './Hero.css';

const StatCard = ({ targetValue, suffix, label, isVisible, delay }) => {
  const [count, setCount] = useState(0);
  const hasAnimatedRef = useRef(false);
  const timerRef = useRef(null);
  const startTimerRef = useRef(null);

  useEffect(() => {
    if (isVisible && !hasAnimatedRef.current) {
      hasAnimatedRef.current = true;
      
      // Pour "24/7", on ne fait pas d'animation
      if (targetValue === null) {
        setCount('24/7');
        return;
      }

      // Délai avant de démarrer l'animation (basé sur le delay prop)
      const startDelay = delay * 1000; // Convertir en millisecondes
      
      startTimerRef.current = setTimeout(() => {
        const duration = 2000; // 2 secondes
        const steps = 60;
        const increment = targetValue / steps;
        const stepDuration = duration / steps;

        let currentStep = 0;
        timerRef.current = setInterval(() => {
          currentStep++;
          const nextValue = Math.min(Math.floor(increment * currentStep), targetValue);
          setCount(nextValue);

          if (nextValue >= targetValue) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            setCount(targetValue);
          }
        }, stepDuration);
      }, startDelay);
    }

    return () => {
      if (startTimerRef.current) {
        clearTimeout(startTimerRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isVisible, targetValue, delay]);

  return (
    <div 
      className={`stat-card ${isVisible ? 'animate' : ''}`}
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="stat-number">
        {targetValue === null ? '24/7' : `${count}${suffix}`}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

// Nom complet de l'entreprise
const COMPANY_NAME = 'Cybernex Services';
const COMPANY_SLOGAN = 'Transformer vos Idées en Expériences Digitales Performantes';
const COMPANY_SERVICES = ['Sites Web', 'Applications Mobiles', 'E-commerce', 'Design', 'Maintenance', 'Consulting'];

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef(null);

  const heroBgStyle = {
    backgroundImage: `url(/IMG2.jpg)`
  };

  const stats = [
    { targetValue: 150, suffix: '+', label: 'Projets réalisés' },
    { targetValue: 100, suffix: '+', label: 'Clients satisfaits' },
    { targetValue: 5, suffix: '+', label: 'Années d\'expérience' },
    { targetValue: null, suffix: '', label: 'Support disponible' } // 24/7 ne s'anime pas
  ];

  useEffect(() => {
    const checkVisibility = () => {
      if (statsRef.current) {
        const rect = statsRef.current.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInView && !isVisible) {
          setIsVisible(true);
        }
      }
    };

    // Vérifier immédiatement
    checkVisibility();

    // Vérifier après un court délai pour s'assurer que le DOM est prêt
    const timeout = setTimeout(checkVisibility, 100);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const currentRef = statsRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      clearTimeout(timeout);
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isVisible]);

  return (
    <section id="hero" className="hero-section section-transition">
      <div className="hero-bg" style={heroBgStyle}></div>
      <div className="hero-content">
        <div className="hero-branding">
          <h1 className="hero-company-name">{COMPANY_NAME}</h1>
          <p className="hero-slogan">{COMPANY_SLOGAN}</p>
          <div className="hero-services">
            {COMPANY_SERVICES.map((service, index) => (
              <span key={index} className="hero-service-tag">{service}</span>
            ))}
          </div>
        </div>
        <div className="hero-stats" ref={statsRef}>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              targetValue={stat.targetValue}
              suffix={stat.suffix}
              label={stat.label}
              isVisible={isVisible}
              delay={index * 0.1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;

